import axios, { isAxiosError, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/lib/env';
import { build } from '@/lib/build';

/**
 * Four axios instances (01-architecture.md §5):
 * - authClient → central auth host (login ONLY; auth/routes/api.php:3-7).
 * - posClient  → current /api/pos surface with Bearer + tenant scope headers.
 * - platformClient → global billing administration on the same API host.
 * - merchantBillingClient → payment status and proof upload outside blocked POS routes.
 *
 * The POS client reads auth state through an INJECTABLE provider so this
 * module never imports a store (no circular imports; tests inject fakes).
 * The lead wires the real provider by constructing AuthStore (its
 * constructor attaches itself) — see src/stores/AuthStore.ts.
 */

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * NAMED ESCAPE HATCH — omit the X-Branch header on this one request.
     *
     * WHY: GET {pos}/day-sessions 500s on EVERY request that sends X-Branch:
     * DaySession's BranchScope adds an unqualified `where('branch_id', …)`
     * (pos-api/app/Models/Scopes/BranchScope.php:18-24) while the index
     * leftJoins `orders`, which also has branch_id — MySQL ambiguous-column
     * error 1052 (pos-api/app/Http/Controllers/Api/POS/DaySessionController.php:337-343).
     *
     * ONLY the day-session history adapter (sessionsApi.list in
     * src/api/sessions.ts) may set this, passing the validated `branch_id`
     * query param instead. Any new use requires a documented backend blocker.
     */
    skipBranchHeader?: boolean;
    /**
     * Omit X-Terminal-Id for organization-wide office reads. The authenticated
     * office role remains required by the backend route; this prevents a
     * remembered register selection from narrowing an admin list to one till.
     */
    skipTerminalHeader?: boolean;
  }
}

/** Scope snapshot injected into every POS request as headers. */
export interface ApiAuthContext {
  token: string | null;
  organizationId: number | null;
  branchId: number | null;
  /** X-Terminal-Id accepts the numeric id or the string identifier; we send the id. */
  terminalId: number | string | null;
  /** X-POS-Terminal is only the per-terminal rate-limit key. */
  terminalIdentifier: string | null;
}

type AuthContextProvider = () => ApiAuthContext | null;
let authContextProvider: AuthContextProvider | null = null;

/** Inject the auth-context getter (AuthStore attaches itself here). */
export function setAuthContextProvider(provider: AuthContextProvider | null): void {
  authContextProvider = provider;
}

/**
 * 401 handling: Passport has no refresh route, so a 401 means the token is
 * dead. The registered handler must clear persisted auth and hard-redirect
 * to /login. Injectable for tests. NOTE: only the POS client triggers this —
 * authClient deliberately has NO 401 interceptor because a login failure is
 * HTTP 400 {"error":"Invalid credentials"} and must never run the logout path.
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/**
 * Terminal invalidation (deleted/deactivated/unassigned while selected):
 * 404 {"error":"Invalid Terminal"} · 403 {"error":"Terminal Inactive"} ·
 * 403 {"error":"Access Denied"} — all from PosTerminalMiddleware
 * (pos-api/app/Http/Middleware/PosTerminalMiddleware.php:32-61). The handler
 * clears the selected terminal and returns the user to /setup.
 */
type TerminalInvalidHandler = () => void;
let terminalInvalidHandler: TerminalInvalidHandler | null = null;
export function setTerminalInvalidHandler(handler: TerminalInvalidHandler | null): void {
  terminalInvalidHandler = handler;
}

export const authClient = axios.create({
  baseURL: env.authBaseUrl,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

export const posClient = axios.create({
  baseURL: env.apiBaseUrl,
  // Accept: application/json is REQUIRED on every call: without it an auth
  // failure tries to redirect to a nonexistent `login` web route and 500s.
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

/**
 * Platform administration is served by the same consolidated sg-sites host,
 * but it deliberately sits outside the tenant-scoped `/api/pos` route group.
 * VITE_API_BASE_URL is contractually the `/api/pos` surface, so replace only
 * that final segment and keep the host/environment identical.
 */
export const platformClient = axios.create({
  baseURL: env.apiBaseUrl.replace(/\/api\/pos\/?$/, '/api/platform/billing'),
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

export const platformRootClient = axios.create({
  baseURL: env.apiBaseUrl.replace(/\/api\/pos\/?$/, '/api/platform'),
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

export const merchantApplicationClient = axios.create({
  baseURL: env.apiBaseUrl.replace(/\/api\/pos\/?$/, '/api/merchant/store-application'),
  headers: { Accept: 'application/json' },
});

export const merchantBillingClient = axios.create({
  baseURL: env.apiBaseUrl.replace(/\/api\/pos\/?$/, '/api/merchant/billing'),
  headers: { Accept: 'application/json' },
});

function attachAuth(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  // Which console build made this request. Informational: server logs and
  // Nightwatch can tell a bug in last week's bundle from one in today's, and
  // it is the hook for a future "minimum supported build" gate. Every call
  // already preflights (JSON content type + scope headers), and the API's
  // CORS allows any request header, so this costs nothing extra.
  config.headers['X-SG-Console-Build'] = build.sha;
  const ctx = authContextProvider?.() ?? null;
  if (ctx?.token) config.headers.Authorization = `Bearer ${ctx.token}`;
  if (ctx?.organizationId != null) config.headers['X-Organization'] = String(ctx.organizationId);
  if (ctx?.branchId != null && !config.skipBranchHeader) {
    config.headers['X-Branch'] = String(ctx.branchId);
  }
  if (ctx?.terminalId != null && !config.skipTerminalHeader) {
    config.headers['X-Terminal-Id'] = String(ctx.terminalId);
  }
  if (ctx?.terminalIdentifier) config.headers['X-POS-Terminal'] = ctx.terminalIdentifier;
  return config;
}

posClient.interceptors.request.use(attachAuth);
platformClient.interceptors.request.use(attachAuth);
platformRootClient.interceptors.request.use(attachAuth);
merchantApplicationClient.interceptors.request.use(attachAuth);
merchantBillingClient.interceptors.request.use(attachAuth);

type SubscriptionRequiredHandler = () => void;
let subscriptionRequiredHandler: SubscriptionRequiredHandler | null = null;
export function setSubscriptionRequiredHandler(handler: SubscriptionRequiredHandler | null): void {
  subscriptionRequiredHandler = handler;
}

export type OrganizationAccessIssue = 'suspended' | 'unavailable' | null;
type OrganizationAccessIssueHandler = (issue: OrganizationAccessIssue) => void;
let organizationAccessIssueHandler: OrganizationAccessIssueHandler | null = null;
export function setOrganizationAccessIssueHandler(
  handler: OrganizationAccessIssueHandler | null,
): void {
  organizationAccessIssueHandler = handler;
}

function handleApiFailure(error: unknown): Promise<never> {
  if (isAxiosError(error) && error.response) {
    const status = error.response.status;
    const body = error.response.data as { error?: unknown; code?: unknown } | undefined;
    const errorKey = typeof body?.error === 'string' ? body.error : undefined;
    const errorCode = typeof body?.code === 'string' ? body.code : undefined;

    if (status === 401) {
      unauthorizedHandler?.();
    } else if (status === 402 && errorKey === 'Subscription payment required') {
      subscriptionRequiredHandler?.();
    } else if (status === 403 && errorCode === 'STORE_SUSPENDED') {
      organizationAccessIssueHandler?.('suspended');
    } else if (
      (status === 403 && errorCode === 'STORE_UNAVAILABLE') ||
      (status === 404 && errorCode === 'ORGANIZATION_NOT_FOUND')
    ) {
      organizationAccessIssueHandler?.('unavailable');
    } else if (
      (status === 403 && (errorKey === 'Terminal Inactive' || errorKey === 'Access Denied')) ||
      (status === 404 && errorKey === 'Invalid Terminal')
    ) {
      terminalInvalidHandler?.();
    }
  }
  return Promise.reject(error);
}

posClient.interceptors.response.use((res) => res, handleApiFailure);
platformClient.interceptors.response.use((res) => res, handleApiFailure);
platformRootClient.interceptors.response.use((res) => res, handleApiFailure);
merchantApplicationClient.interceptors.response.use((res) => res, handleApiFailure);
merchantBillingClient.interceptors.response.use((res) => res, (error: unknown) => {
  if (isAxiosError(error) && error.response) {
    const body = error.response.data as { code?: unknown } | undefined;
    const code = typeof body?.code === 'string' ? body.code : undefined;
    if (error.response.status === 401) unauthorizedHandler?.();
    else if (error.response.status === 403 && code === 'STORE_SUSPENDED') {
      organizationAccessIssueHandler?.('suspended');
    } else if (
      (error.response.status === 403 && code === 'STORE_UNAVAILABLE') ||
      (error.response.status === 404 && code === 'ORGANIZATION_NOT_FOUND')
    ) {
      organizationAccessIssueHandler?.('unavailable');
    }
  }
  return Promise.reject(error);
});
